import React from "react";
import {Link} from "react-router-dom";
import {FormattedMessage, useIntl} from "react-intl";
import {GroupDetailResponse} from "../../../logic/APITypes";
import {Resolution} from "../../../logic/API";
import {LoadingSkeleton} from "@spg/component_library";
import ContentTile from "../../../components/ContentTile";
import useAppActions from "../../../hooks/useAppActions";
import useInitialAppStatus from "../../../hooks/useInitialAppStatus";
import LoadingSpinner from "../../../components/LoadingSpinner";
import useAvailableApps from "../../../hooks/useAvailableApps";
import useInstalledApps from "../../../hooks/useInstalledApps";
import InstalledApp from "../apps/InstalledApp";

type DetailAppsProps = {
  group: Resolution<GroupDetailResponse>;
  isGroupAdmin: boolean;
  groupId: string;
};

/** The section of the details page linking to apps and offering admins the ability to manage them */
function DetailsApps({group, groupId, isGroupAdmin}: DetailAppsProps) {
  const {formatMessage} = useIntl();

  const {installedApps, loading: installedAppsLoading} =
    useInstalledApps(groupId);
  const {availableApps, loading: availableAppsLoading} =
    useAvailableApps(groupId);

  const appActions = isGroupAdmin
    ? useAppActions(groupId)
    : {
        loadingStates: {} as Record<string, boolean>,
        installApp: () => undefined,
        cancelInstall: () => undefined,
      };

  const initialAppStatus = isGroupAdmin
    ? useInitialAppStatus({
        groupId,
        apps: availableApps.map((app) => ({appId: app.id, isInstalled: false})),
      })
    : {loadingStates: {}};

  const {loadingStates, installApp, cancelInstall} = appActions;
  const {loadingStates: initialLoadingStates} = initialAppStatus;

  return (
    <>
      <h2>
        <FormattedMessage id="groupDetails.header.apps" />
      </h2>
      <h3>
        <FormattedMessage id="groupDetails.header.installed" />
      </h3>
      <div className="groupDetails__installedApps">
        {installedAppsLoading ? (
          <div className="groupDetails__skeleton--installed">
            <LoadingSkeleton />
          </div>
        ) : installedApps.length === 0 ? (
          <i className="groupDetails__noItems">
            <FormattedMessage id="groupDetails.paragraph.noInstalledApps" />
          </i>
        ) : (
          installedApps.map((app) => (
            <InstalledApp
              key={app.id}
              isGroupAdmin={group.payload?.is_admin ?? false}
              groupName={group.payload?.name}
              from="group"
              {...app}
            />
          ))
        )}
      </div>
      <h3>
        <FormattedMessage id="groupDetails.header.available" />
      </h3>
      <div className="groupDetails__availableApps">
        {availableAppsLoading ? (
          <div className="groupDetails__skeleton--available">
            <LoadingSkeleton />
          </div>
        ) : availableApps.length === 0 ? (
          <i className="groupDetails__noItems">
            <FormattedMessage id="groupDetails.paragraph.noAvailableApps" />
          </i>
        ) : (
          availableApps.map((app) => (
            <ContentTile
              key={app.id}
              name={app.name}
              description={app.description}
              clickElement={
                !isGroupAdmin ? undefined : app.requires_parameters &&
                  !(loadingStates[app.id] || initialLoadingStates[app.id]) ? (
                  <Link
                    id={`install_${app.name}_button`}
                    to={`/groups/${groupId}/apps/available/${app.id}/parameters`}
                    state={{from: "group"}}
                    className="linkButton"
                    aria-label={formatMessage(
                      {
                        id: "general.link.ariaLabel.internal",
                      },
                      {name: app.name},
                    )}
                  >
                    <FormattedMessage id="general.button.label.install" />
                  </Link>
                ) : (
                  <button
                    id={`${loadingStates[app.id] || initialLoadingStates[app.id] ? "cancel" : "install"}_${app.name}_button`}
                    onClick={() =>
                      loadingStates[app.id] || initialLoadingStates[app.id]
                        ? cancelInstall(app.id)
                        : installApp(app.id)
                    }
                    className="linkButton"
                    disabled={initialLoadingStates[app.id] === undefined}
                  >
                    <FormattedMessage
                      id={
                        loadingStates[app.id] || initialLoadingStates[app.id]
                          ? "general.button.label.cancel"
                          : "general.button.label.install"
                      }
                    />
                  </button>
                )
              }
              additionalInfo={
                loadingStates[app.id] || initialLoadingStates[app.id] ? (
                  <div className="groupDetails__appsLoading">
                    <FormattedMessage id="infoTile.status.install" />
                    <LoadingSpinner />
                  </div>
                ) : (
                  <p className="groupDetails__appsVersion">
                    <b>
                      <FormattedMessage id="apps.version" />
                    </b>
                    {app.app_version}
                  </p>
                )
              }
              image={app.logo}
              internalLink={{
                to: `/groups/${groupId}/apps/available/${app.id}`,
                state: {
                  isGroupAdmin: group.payload?.is_admin,
                  groupName: group.payload?.name,
                },
              }}
            />
          ))
        )}
      </div>
    </>
  );
}

export default DetailsApps;
